import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { formatarData, formatarReais, pad4 } from '@/pages/talao/utils';
import Card from '@/components/ui/Card';
import Chip from '@/components/ui/Chip';
import StatCard from '@/components/ui/StatCard';
import ProgressRing from '@/components/ui/ProgressRing';
import Accordion from '@/components/ui/Accordion';
import Sheet from '@/components/ui/Sheet';
import ScreenHeader from '@/components/ui/ScreenHeader';
import Dica from '@/components/feature/Dica';
import BlocoQR from './components/BlocoQR';
import AvisosTalao from './components/AvisosTalao';
import GradeNumeros from './components/GradeNumeros';
import Venda from './components/Venda';
import SeletorVendedor from './components/SeletorVendedor';
import type { CapaSorteio, MeuTalao, TalaoNovo } from './types';

const INTERVALO_POLLING_MS = 10000;
const DURACAO_AVISO_MS = 4000;

type ChipTipo = 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'neutro';

// Mapeia o status da rifa para o chip do cabeçalho.
function chipDoStatus(status: string | null): { tipo: ChipTipo; rotulo: string } {
  switch (status) {
    case 'publicada':
      return { tipo: 'success', rotulo: 'Vendendo' };
    case 'sorteada':
    case 'encerrada':
      return { tipo: 'info', rotulo: 'Sorteada' };
    case 'cancelada':
      return { tipo: 'neutro', rotulo: 'Cancelada' };
    default:
      return { tipo: 'success', rotulo: 'Vendendo' };
  }
}

export default function MeuTalaoPage() {
  const { rifaId } = useParams<{ rifaId: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [t, setT] = useState<MeuTalao | null>(null);
  const [capa, setCapa] = useState<CapaSorteio | null>(null);

  const [pegando, setPegando] = useState(false);
  const [erroPegar, setErroPegar] = useState<string | null>(null);
  const [avisoPegar, setAvisoPegar] = useState<string | null>(null);

  const [aviso, setAviso] = useState<string | null>(null);
  const pagosRef = useRef<Set<number> | null>(null);
  const avisoTimer = useRef<number | null>(null);
  const vendedorIdRef = useRef<number | null>(null);
  const [vendedorAtual, setVendedorAtual] = useState<number | null>(null);
  const [qrAberto, setQrAberto] = useState(false);

  const [searchParams] = useSearchParams();
  const abrirQRInicial = searchParams.get('cartela') === '1';

  const [estado, setEstado] = useState<'lista' | 'venda'>('lista');

  async function carregar(pVendedor?: number) {
    const params: Record<string, number> = { p_rifa: Number(rifaId) };
    const vendedor = pVendedor ?? vendedorIdRef.current ?? null;
    if (vendedor != null) params.p_vendedor = vendedor;
    const { data, error } = await supabase.rpc('meu_talao', params);
    if (error) throw error;
    return (data as MeuTalao) ?? null;
  }

  function mostrarAviso(msg: string) {
    setAviso(msg);
    if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    avisoTimer.current = window.setTimeout(() => setAviso(null), DURACAO_AVISO_MS);
  }

  // Carregamento inicial.
  useEffect(() => {
    let ativo = true;

    (async () => {
      setLoading(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/meu-talao/${rifaId}`)}`, {
            replace: true,
          });
          return;
        }

        await garantirUsuario();

        const talao = await carregar();
        if (!ativo) return;
        setT(talao);
        if (talao) {
          const sel =
            (talao.fichas || []).find((f) => f.selecionada) ??
            (talao.fichas || [])[0] ??
            null;
          vendedorIdRef.current = sel?.vendedor_id ?? null;
          setVendedorAtual(sel?.vendedor_id ?? null);
          pagosRef.current = new Set(
            (talao.numeros || []).filter((n) => n.status === 'paga').map((n) => n.numero),
          );
        }
      } catch (e) {
        if (ativo) {
          setErro(
            e instanceof Error ? e.message : 'Não foi possível carregar. Tente novamente.',
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    })();

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rifaId, navigate, pronto, user]);

  // Capa do sorteio (título + imagem do fundo) — não bloqueia o talão.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [resPublica, resCapa] = await Promise.all([
          supabase
            .from('vw_rifa_publica')
            .select('titulo, sorteio_em, status')
            .eq('rifa_id', Number(rifaId))
            .maybeSingle(),
          supabase.rpc('capa_da_rifa', { p_rifa: Number(rifaId) }),
        ]);
        const base = (resPublica.data ?? null) as {
          titulo: string | null;
          sorteio_em: string | null;
          status: string | null;
        } | null;
        const capaData = (resCapa.data ?? null) as { imagem?: string | null } | null;
        if (!ativo) return;
        if (base || capaData) {
          setCapa({
            titulo: base?.titulo ?? null,
            imagem: capaData?.imagem ?? null,
            sorteio_em: base?.sorteio_em ?? null,
            status: base?.status ?? null,
          });
        }
      } catch {
        /* silencioso: sem capa, o talão segue com o gradiente */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [rifaId]);

  // Atualização automática a cada 10s.
  useEffect(() => {
    if (!t) return;
    const intervalo = window.setInterval(async () => {
      try {
        const novo = await carregar();
        if (!novo) return;
        setT(novo);
        const agora = new Set(
          (novo.numeros || []).filter((n) => n.status === 'paga').map((n) => n.numero),
        );
        const anterior = pagosRef.current ?? new Set<number>();
        pagosRef.current = agora;

        const novos = [...agora].filter((num) => !anterior.has(num));
        if (novos.length > 0) {
          const n = novo.numeros.find((x) => x.numero === novos[0]);
          mostrarAviso(`Vendeu! Número ${pad4(novos[0])}${n?.comprador ? ` — ${n.comprador}` : ''}`);
        }
      } catch {
        // silencioso: o próximo tick tenta de novo
      }
    }, INTERVALO_POLLING_MS);

    return () => window.clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.codigo]);

  useEffect(
    () => () => {
      if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    },
    [],
  );

  async function pegarOutroTalao() {
    setPegando(true);
    setErroPegar(null);
    setAvisoPegar(null);
    try {
      const { data, error } = await supabase.rpc('pegar_talao', { p_rifa: Number(rifaId) });
      if (error) throw error;
      const linhas = (data as unknown[]) || [];
      if (linhas.length === 0) {
        setErroPegar('Não foi possível pegar um talão agora.');
        return;
      }
      const novo = linhas[0] as TalaoNovo;
      if (novo.situacao) setAvisoPegar(novo.situacao);

      const t2 = await carregar();
      if (t2) {
        setT(t2);
        pagosRef.current = new Set(
          (t2.numeros || []).filter((n) => n.status === 'paga').map((n) => n.numero),
        );
      }
    } catch (e) {
      setErroPegar(e instanceof Error ? e.message : 'Não foi possível pegar o talão.');
    } finally {
      setPegando(false);
    }
  }

  async function recarregarTalao() {
    const t2 = await carregar();
    if (t2) {
      setT(t2);
      pagosRef.current = new Set(
        (t2.numeros || []).filter((n) => n.status === 'paga').map((n) => n.numero),
      );
    }
  }

  async function trocarVendedor(id: number) {
    vendedorIdRef.current = id;
    setVendedorAtual(id);
    const t2 = await carregar(id);
    if (t2) {
      setT(t2);
      pagosRef.current = new Set(
        (t2.numeros || []).filter((n) => n.status === 'paga').map((n) => n.numero),
      );
    }
  }

  async function cancelarVenda(numero: number) {
    const { error } = await supabase.rpc('cancelar_venda', {
      p_rifa: Number(rifaId),
      p_numeros: [numero],
    });
    if (error) throw error;
    await recarregarTalao();
  }

  function abrirVenda() {
    setQrAberto(false);
    setEstado('venda');
  }

  function sairDaVenda() {
    setEstado('lista');
    recarregarTalao();
  }

  const link = useMemo(
    () => (t?.token ? `${window.location.origin}/t/${t.token}` : ''),
    [t?.token],
  );

  const talaoAtivo = useMemo(
    () => (t?.taloes || []).find((x) => x.status === 'ativo') ?? (t?.taloes || [])[0] ?? null,
    [t],
  );

  async function mandarLinkWhats() {
    if (!t?.token) return;
    try {
      const { data } = await supabase.rpc('mensagem_de_venda', { p_token: t.token });
      const res = data as { completa?: string } | null;
      const texto = res?.completa || link;
      window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, '_blank', 'noopener,noreferrer');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-5 py-6">
          <div className="h-8 w-48 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-5 h-56 w-full animate-pulse rounded-[22px] bg-background-200/70" />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
            <div className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível carregar
          </h1>
          <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Tentar novamente
          </button>
        </Card>
      </div>
    );
  }

  if (!t) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-ticket-2-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Você não tem talão nesta ação entre amigos.
          </h1>
        </Card>
      </div>
    );
  }

  const numeros = t.numeros || [];
  const vendidos = numeros.filter((n) => n.status === 'paga').length;
  const esperando = numeros.filter((n) => n.status === 'reservada').length;
  const livres = numeros.filter((n) => n.status === 'livre').length;
  const total = numeros.length;
  const percentual = total > 0 ? Math.round((vendidos / total) * 100) : 0;
  const esgotado = livres === 0 && esperando === 0;

  const fichas = t.fichas || [];
  const mostrarSeletor = (t.quantas_fichas ?? 0) > 1;
  const fichaSelecionadaId =
    fichas.find((f) => f.selecionada)?.vendedor_id ?? fichas[0]?.vendedor_id ?? 0;

  const chip = chipDoStatus(capa?.status ?? null);
  const subtitulo = capa?.sorteio_em ? `Resultado em ${formatarData(capa.sorteio_em)}` : undefined;

  // Anel de progresso: dívida da inscrição (quando houver) ou progresso de vendas.
  const custa = t.inscricao_custa_reais ?? 0;
  const falta = Math.max(0, t.ainda_falta_reais ?? 0);
  const jaPagou = t.ja_pagou_reais ?? 0;
  const temInscricao = t.abate_inscricao && custa > 0;
  const valorCota = t.valor_cota_reais ?? 0;
  const faltamNumeros =
    temInscricao && valorCota > 0 ? Math.ceil(falta / valorCota) : livres;

  const anelValor = temInscricao
    ? Math.min(100, custa > 0 ? (jaPagou / custa) * 100 : 0)
    : percentual;

  const fraseAbaixo = temInscricao
    ? falta === 0
      ? 'Sua inscrição está quitada.'
      : `Faltam ${faltamNumeros} ${faltamNumeros === 1 ? 'número' : 'números'} para quitar sua inscrição.`
    : `Faltam ${livres} ${livres === 1 ? 'número' : 'números'} para vender.`;

  return (
    <div className={`min-h-screen bg-background-100 ${esgotado ? 'pb-10' : 'pb-32'}`}>
      <ScreenHeader
        titulo={capa?.titulo || 'Meu talão'}
        subtitulo={subtitulo}
        voltar
        acao={
          <div className="flex items-center gap-2">
            <Chip tipo={chip.tipo}>{chip.rotulo}</Chip>
            <Dica id="meu-talao" />
          </div>
        }
      />

      <main className="mx-auto w-full max-w-[720px] px-5 py-5">
        {aviso && (
          <div className="app-entra fixed left-1/2 top-20 z-50 w-[calc(100%-2.5rem)] max-w-md -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-[18px] bg-success-50 px-4 py-3 text-[14px] font-semibold text-success-800 shadow-pcard">
              <span className="flex h-5 w-5 items-center justify-center text-success-600">
                <i className="ri-check-double-line" aria-hidden />
              </span>
              {aviso}
            </div>
          </div>
        )}

        {estado === 'venda' ? null : <AvisosTalao />}

        {estado === 'venda' ? null : esgotado ? (
          <Card className="app-entra p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center text-success-500">
              <i className="ri-checkbox-circle-line text-[44px]" aria-hidden />
            </span>
            <h1 className="mt-3 text-[20px] font-extrabold text-foreground-950">
              Você vendeu o talão inteiro!
            </h1>
            <button
              type="button"
              onClick={pegarOutroTalao}
              disabled={pegando}
              className="aperta mt-5 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white disabled:opacity-60"
            >
              {pegando ? 'Pegando…' : 'Pegar outro talão'}
            </button>
            {avisoPegar && <p className="mt-3 text-[14px] text-foreground-600">{avisoPegar}</p>}
            {erroPegar && <p className="mt-3 text-[14px] text-danger-600">{erroPegar}</p>}
          </Card>
        ) : (
          <>
            {mostrarSeletor && (
              <div className="app-entra mb-3">
                <p className="mb-1.5 text-[13px] font-semibold text-foreground-500">
                  Você está vendendo por:
                </p>
                <SeletorVendedor
                  fichas={fichas}
                  selecionadaId={fichaSelecionadaId}
                  aoTrocar={trocarVendedor}
                />
              </div>
            )}

            {/* Anel de progresso */}
            <Card className="app-entra flex flex-col items-center p-6">
              <ProgressRing valor={anelValor} tamanho={196} espessura={15}>
                {temInscricao ? (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                      Falta
                    </p>
                    <p className="mt-0.5 whitespace-nowrap text-[24px] font-extrabold leading-none text-foreground-950">
                      {formatarReais(falta)}
                    </p>
                    <p className="mt-1 text-[12px] text-foreground-500">
                      de {formatarReais(custa)}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="whitespace-nowrap text-[30px] font-extrabold leading-none text-foreground-950">
                      {percentual}%
                    </p>
                    <p className="mt-1 text-[12px] text-foreground-500">
                      {vendidos} de {total}
                    </p>
                  </>
                )}
              </ProgressRing>
              <p className="mt-5 text-center text-[14px] text-foreground-600">{fraseAbaixo}</p>
            </Card>

            {/* 2 cartões */}
            <div className="app-entra mt-3 grid grid-cols-2 gap-3" style={{ animationDelay: '50ms' }}>
              <StatCard rotulo="Vendidos" valor={vendidos} icone="ri-check-double-line" />
              <StatCard rotulo="Arrecadado" valor={formatarReais(t.arrecadou_reais)} />
            </div>

            {/* Números e histórico (recolhido) */}
            <div className="app-entra mt-3" style={{ animationDelay: '100ms' }}>
              <Accordion titulo="Ver meus números" resumo={`${vendidos}/${total}`}>
                <GradeNumeros t={t} aoAbrirVenda={abrirVenda} aoCancelarVenda={cancelarVenda} />
              </Accordion>
            </div>
          </>
        )}
      </main>

      {/* Rodapé fixo */}
      {!esgotado && estado !== 'venda' && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-background-200 bg-background-50/95 backdrop-blur-md"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto flex w-full max-w-[720px] flex-col gap-2 px-5 py-3">
            <button
              type="button"
              onClick={mandarLinkWhats}
              disabled={!t.token}
              className="aperta flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-success-500 text-[15px] font-bold text-white transition-colors hover:bg-success-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-whatsapp-line" aria-hidden />
              </span>
              Enviar meu link no WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setQrAberto(true)}
              className="aperta flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] border border-background-200 bg-background-50 text-[15px] font-semibold text-foreground-900 shadow-pcard transition-colors hover:bg-background-100"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-qr-code-line" aria-hidden />
              </span>
              Vender na hora (QR)
            </button>
          </div>
        </div>
      )}

      {/* Folha do QR para vender / registrar venda */}
      <Sheet
        aberto={qrAberto}
        titulo="Vender na hora"
        aoFechar={() => setQrAberto(false)}
        rodape={
          <button
            type="button"
            onClick={abrirVenda}
            className="aperta flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-add-line" aria-hidden />
            </span>
            Registrar venda no Pix
          </button>
        }
      >
        <BlocoQR
          t={t}
          link={link}
          talaoId={talaoAtivo?.talao_id ?? null}
          rifaId={Number(rifaId)}
          vendedorId={vendedorAtual}
          abrirQRInicial
        />
      </Sheet>

      {estado === 'venda' && (
        <Venda
          t={t}
          rifaId={Number(rifaId)}
          vendedorId={vendedorIdRef.current}
          aoSair={sairDaVenda}
          aoConcluir={recarregarTalao}
        />
      )}
    </div>
  );
}